import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock3,
  FileUp,
  Info,
  Palmtree,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  BalanceSummary,
  LeaveEntry,
  LeaveType,
  Paginated,
  Team,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage, hasUncertainApiOutcome } from "../utils/apiError";
import { formatDate, formatMinutes, organizationQuery } from "../utils/format";
import DateField from "../components/DateField";

type LeavePool = {
  enabled: boolean;
  leave_type_ids: number[];
  label: string;
};

type Preview = {
  days: Array<{
    date: string;
    scheduled_minutes: number;
    requested_minutes: number;
    is_workday: boolean;
    public_holiday: string | null;
    public_holiday_deducted: boolean;
    school_holidays: string[];
  }>;
  requested_minutes: number;
  balance_change_minutes: number;
  balance_leave_type: string;
  balance_is_pooled?: boolean;
  current_balance_minutes: number;
  balance_after_minutes: number;
  negative_balance_allowed: boolean;
  school_holidays: string[];
  school_region: "north" | "middle" | "south" | "";
  school_regions?: Array<"north" | "middle" | "south">;
  school_holidays_are_informational: true;
};

export default function LeaveRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { entryId } = useParams();
  const isEditing = Boolean(entryId);
  const { organizationId, organization, capabilities } = useWorkspace();
  const initialDate =
    searchParams.get("start_date") ??
    searchParams.get("date") ??
    new Date().toISOString().slice(0, 10);
  const initialEndDate = searchParams.get("end_date") ?? initialDate;
  const returnTo = (() => {
    const value = searchParams.get("return_to");
    return value?.startsWith("/app/") ? value : "/app/requests";
  })();
  const requestedType = searchParams.get("type");
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<BalanceSummary[]>([]);
  const [leavePool, setLeavePool] = useState<LeavePool>({
    enabled: false,
    leave_type_ids: [],
    label: "",
  });
  const [teams, setTeams] = useState<Team[]>([]);
  const [leaveType, setLeaveType] = useState<number | null>(null);
  const [team, setTeam] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [durationMode, setDurationMode] = useState<
    "whole_day" | "partial_day" | "manual"
  >("whole_day");
  const [hours, setHours] = useState("4");
  const [overtimeFactor, setOvertimeFactor] = useState("1.25");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const organizationPart = organizationQuery(organizationId);
    const suffix = organizationPart ? `?${organizationPart}` : "";
    setLoading(true);
    void Promise.all([
      api.get<Paginated<LeaveType> | LeaveType[]>(`/leave/types/${suffix}`),
      api.get(`/leave/balances/overview/${suffix}`),
      organizationId
        ? api.get<Paginated<Team> | Team[]>("/teams/")
        : Promise.resolve({ data: [] as Team[] }),
    ])
      .then(([typeResponse, balanceResponse, teamResponse]) => {
        const types = unwrapResults(typeResponse.data);
        const pool: LeavePool = balanceResponse.data.leave_pool ?? {
          enabled: false,
          leave_type_ids: [],
          label: "",
        };
        setLeavePool(pool);
        // Bij een gezamenlijke pot kiest de app zelf; de losse ATV- en
        // verlofregels verdwijnen dan uit de keuzelijst.
        const selectableTypes = pool.enabled
          ? types.filter(
              (row) =>
                !pool.leave_type_ids.includes(row.id) ||
                row.id === pool.leave_type_ids[pool.leave_type_ids.length - 1],
            )
          : types;
        setLeaveTypes(selectableTypes);
        setLeaveType((current) =>
          current && selectableTypes.some((row) => row.id === current)
            ? current
            : selectableTypes.find(
                (row) => row.code === requestedType || row.category === requestedType,
              )?.id ??
              selectableTypes.find((row) => row.category === "leave")?.id ??
              selectableTypes[0]?.id ??
              null,
        );
        setBalances(balanceResponse.data.summary);
        const teamRows = unwrapResults(teamResponse.data).filter(
          (row) => row.organization === organizationId,
        );
        setTeams(teamRows);
        setTeam((current) => current ?? teamRows[0]?.id ?? null);
      })
      .catch(() => setError("De aanvraaggegevens konden niet worden geladen."))
      .finally(() => setLoading(false));
  }, [organizationId, requestedType]);

  useEffect(() => {
    if (!entryId) return;
    setLoading(true);
    void api
      .get<LeaveEntry>(`/leave/entries/${entryId}/`)
      .then((response) => {
        const entry = response.data;
        setLeaveType(entry.leave_type);
        setTeam(entry.team ?? null);
        setStartDate(entry.start_date);
        setEndDate(entry.end_date);
        setDurationMode(entry.duration_mode);
        if (entry.duration_mode !== "whole_day") {
          setHours(String(Number(entry.input_value)));
        }
        setOvertimeFactor(String(Number(entry.overtime_factor ?? 1)));
        setReason(entry.reason ?? "");
        setNote(entry.note ?? "");
      })
      .catch(() => setError("Dit verlof kon niet worden geladen."))
      .finally(() => setLoading(false));
  }, [entryId]);

  const pooledTypeId =
    leavePool.leave_type_ids[leavePool.leave_type_ids.length - 1] ?? null;
  const selectedType = leaveTypes.find((row) => row.id === leaveType);
  const isOvertime = selectedType?.category === "overtime";
  const isTimeForTime = selectedType?.category === "time_for_time";
  // Onbetaald verlof heeft geen pot: saldoregels en saldowaarschuwingen zijn
  // dan misleidend.
  const usesBalance = selectedType?.requires_balance !== false;
  const selectedBalance = balances.find((row) => row.leave_type_id === leaveType);

  useEffect(() => {
    if (isOvertime) {
      setDurationMode("manual");
      setOvertimeFactor(String(selectedType?.default_overtime_factor ?? "1"));
    }
  }, [isOvertime, selectedType?.default_overtime_factor]);

  useEffect(() => {
    if (isOvertime) setEndDate(startDate);
  }, [isOvertime, startDate]);
  const previewPayload = useMemo(
    () => ({
      organization: organizationId,
      team: organizationId ? team : null,
      leave_type: leaveType,
      start_date: startDate,
      end_date: durationMode === "whole_day" ? endDate : startDate,
      input_unit: durationMode === "whole_day" ? "days" : "hours",
      input_value: durationMode === "whole_day" ? "1" : hours,
      duration_mode: durationMode,
      overtime_factor: isOvertime ? overtimeFactor : "1",
      reason,
      note,
    }),
    [
      organizationId,
      team,
      leaveType,
      startDate,
      endDate,
      durationMode,
      hours,
      overtimeFactor,
      isOvertime,
      reason,
      note,
    ],
  );

  useEffect(() => {
    if (
      !leaveType ||
      !startDate ||
      !endDate ||
      (durationMode !== "whole_day" && !hours)
    ) {
      setPreview(null);
      return;
    }
    const timeout = window.setTimeout(() => {
      void api
        .post<Preview>("/leave/entries/preview/", previewPayload)
        .then((response) => {
          setPreview(response.data);
          setError("");
        })
        .catch((requestError: unknown) => {
          setError(
            apiErrorMessage(requestError, "Controleer de gekozen periode en uren."),
          );
          setPreview(null);
        });
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [
    previewPayload,
    leaveType,
    startDate,
    endDate,
    durationMode,
    hours,
    overtimeFactor,
  ]);

  // Een dag zonder roosteruren levert bij Hele dag en Gedeeltelijk 0 uur op.
  // Dat mag niet stilzwijgend gebeuren, dus we tonen het en blokkeren opslaan.
  const unscheduledDays = useMemo(
    () => preview?.days.filter((day) => day.scheduled_minutes === 0) ?? [],
    [preview],
  );
  const bookedWithoutSchedule = unscheduledDays.some(
    (day) => day.requested_minutes > 0,
  );
  const hasNoRequestedHours = Boolean(preview) && preview?.requested_minutes === 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!leaveType || !preview || hasNoRequestedHours) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const endpoint = isEditing ? `/leave/entries/${entryId}/` : "/leave/entries/";
      if (attachment) {
        const form = new FormData();
        Object.entries(previewPayload).forEach(([key, value]) => {
          if (value !== null && value !== undefined) form.append(key, String(value));
        });
        form.append("attachment", attachment);
        await (isEditing
          ? api.patch<LeaveEntry>(endpoint, form)
          : api.post<LeaveEntry>(endpoint, form));
      } else {
        await (isEditing
          ? api.patch<LeaveEntry>(endpoint, previewPayload)
          : api.post<LeaveEntry>(endpoint, previewPayload));
      }
      const message = isEditing
        ? "De wijziging is opgeslagen."
        : isOvertime
        ? organizationId
          ? "Je overuren staan als concept klaar om in te dienen."
          : "Je overuren staan als concept klaar om te registreren."
        : organizationId
        ? "Je verlof staat als concept klaar om in te dienen."
        : "Je verlof staat als concept klaar om te registreren.";
      setSuccess(message);
      navigate(returnTo, { replace: true });
    } catch (requestError: unknown) {
      if (hasUncertainApiOutcome(requestError)) {
        const context = organizationId
          ? `?organization=${organizationId}`
          : "?context=personal";
        const persisted = await api
          .get<Paginated<LeaveEntry> | LeaveEntry[]>(`/leave/entries/${context}`)
          .then((response) =>
            unwrapResults(response.data).some(
              (entry) =>
                entry.leave_type === leaveType &&
                entry.start_date === startDate &&
                entry.end_date ===
                  (durationMode === "whole_day" ? endDate : startDate) &&
                entry.requested_minutes === preview.requested_minutes,
            ),
          )
          .catch(() => false);
        if (persisted) {
          navigate(returnTo, { replace: true });
          return;
        }
      }
      setError(
        apiErrorMessage(requestError, "De aanvraag kon niet worden opgeslagen."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState label="Aanvraagformulier laden..." />;
  if (organizationId && !capabilities.canRequestLeave) {
    return (
      <div>
        <PageHeader
          title="Verlof aanvragen"
          description="Deze werkruimte staat alleen-lezen."
        />
        <ErrorNotice>
          Een actief Team- of Business-abonnement is nodig voor nieuwe aanvragen.
        </ErrorNotice>
      </div>
    );
  }

  return (
    <div className="leave-request-page">
      <PageHeader
        eyebrow={organization?.name ?? "Persoonlijke registratie"}
        title={
          isEditing
            ? isOvertime
              ? "Overuren wijzigen"
              : "Verlof wijzigen"
            : isOvertime
            ? "Overuren registreren"
            : organization
            ? "Verlof aanvragen"
            : "Verlof registreren"
        }
        description={
          isEditing
            ? "Pas de periode of het aantal uren aan; het saldo wordt opnieuw geboekt."
            : isOvertime
            ? "Leg de gewerkte uren en de afgesproken factor vast."
            : "Uren worden berekend op basis van je werkpatroon en feestdagenbeleid."
        }
        actions={
          <Link className="button subtle" to={returnTo}>
            <ArrowLeft size={17} /> Terug
          </Link>
        }
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      <form className="request-layout" onSubmit={submit}>
        <section className="surface request-form">
          <div className="form-section">
            <h2>Soort verlof</h2>
            <label>
              <span>Verloftype</span>
              <select
                value={leaveType ?? ""}
                onChange={(event) => setLeaveType(Number(event.target.value))}
                required
              >
                {leaveTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {leavePool.enabled && pooledTypeId === type.id
                      ? `Automatisch (${leavePool.label})`
                      : type.name}
                  </option>
                ))}
              </select>
            </label>
            {leavePool.enabled && pooledTypeId === leaveType && (
              <div className="leave-pool-notice">
                <Info size={18} />
                <p>
                  ATV en verlof tellen als een pot. De app gebruikt eerst je ATV-uren
                  en daarna je verlofuren.
                </p>
              </div>
            )}
            {organizationId && teams.length > 0 && (
              <label>
                <span>Team</span>
                <select
                  value={team ?? ""}
                  onChange={(event) => setTeam(Number(event.target.value))}
                  required
                >
                  {teams.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="form-section">
            <h2>{isOvertime ? "Gewerkte uren" : "Periode"}</h2>
            <div className="date-field-grid">
              <label>
                <span>Van</span>
                <DateField
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    if (endDate < value) setEndDate(value);
                  }}
                  required
                />
              </label>
              <label>
                <span>Tot</span>
                <DateField
                  value={endDate}
                  min={startDate}
                  disabled={isOvertime || durationMode !== "whole_day"}
                  onChange={(value) => setEndDate(value)}
                  required
                />
              </label>
            </div>
            {!isOvertime && (
              <div className="segmented-control duration-control" aria-label="Duur">
                <button
                  type="button"
                  className={durationMode === "whole_day" ? "active" : ""}
                  onClick={() => setDurationMode("whole_day")}
                >
                  Hele dag
                </button>
                <button
                  type="button"
                  className={durationMode === "partial_day" ? "active" : ""}
                  onClick={() => setDurationMode("partial_day")}
                >
                  Gedeeltelijk
                </button>
                <button
                  type="button"
                  className={durationMode === "manual" ? "active" : ""}
                  onClick={() => setDurationMode("manual")}
                >
                  Handmatig
                </button>
              </div>
            )}
            {durationMode !== "whole_day" && (
              <label>
                <span>Aantal uren</span>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={hours}
                  onChange={(event) => setHours(event.target.value)}
                  required
                />
              </label>
            )}
            {isOvertime && (
              <label>
                <span>Overwerkfactor</span>
                <input
                  type="number"
                  min="0.01"
                  max="3"
                  step="0.05"
                  value={overtimeFactor}
                  onChange={(event) => setOvertimeFactor(event.target.value)}
                  required
                />
              </label>
            )}
          </div>

          <div className="form-section">
            <h2>Toelichting</h2>
            <label>
              <span>
                Reden <small>optioneel</small>
              </span>
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={240}
                placeholder="Bijvoorbeeld vakantie"
              />
            </label>
            <label>
              <span>
                Opmerking <small>optioneel</small>
              </span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={4}
                placeholder="Extra informatie voor je manager"
              />
            </label>
            <label className="file-field">
              <FileUp size={18} />
              <span>{attachment ? attachment.name : "Bijlage toevoegen"}</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                onChange={(event) => setAttachment(event.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </section>

        <aside className="request-summary">
          <section className="surface sticky-summary">
            <div className="summary-heading">
              <Palmtree size={20} />
              <div>
                <span>{selectedType?.name}</span>
                <strong>Berekening</strong>
              </div>
            </div>
            <div className="calculation-list">
              <div>
                <span>{isOvertime ? "Datum" : "Werkdagen"}</span>
                <strong>
                  {preview?.days.filter((day) => day.requested_minutes > 0).length ?? 0}
                </strong>
              </div>
              <div>
                <span>
                  {isOvertime
                    ? "Gewerkte overuren"
                    : isTimeForTime
                    ? "Opname"
                    : "Verlofuren"}
                </span>
                <strong>{formatMinutes(preview?.requested_minutes ?? 0)}</strong>
              </div>
              {isOvertime && (
                <div>
                  <span>Bijschrijving (factor {overtimeFactor})</span>
                  <strong>
                    +{formatMinutes(preview?.balance_change_minutes ?? 0)}
                  </strong>
                </div>
              )}
              {usesBalance && (
                <>
                  <div>
                    <span>
                      {preview?.balance_leave_type ?? selectedType?.name} saldo
                    </span>
                    <strong>
                      {formatMinutes(
                        preview?.current_balance_minutes ??
                          selectedBalance?.current_minutes ??
                          0,
                      )}
                    </strong>
                  </div>
                  <div className="calculation-total">
                    <span>Saldo na registratie</span>
                    <strong>
                      {formatMinutes(
                        preview?.balance_after_minutes ??
                          selectedBalance?.available_minutes ??
                          0,
                      )}
                    </strong>
                  </div>
                </>
              )}
            </div>
            {!usesBalance && (
              <div className="notice notice-info compact">
                <Info size={17} />
                <span>
                  {selectedType?.name} kent geen saldo. De uren worden alleen
                  vastgelegd en zijn terug te zien bij Saldo.
                </span>
              </div>
            )}
            {hasNoRequestedHours && (
              <div className="notice compact notice-warning">
                <AlertTriangle size={17} />
                <span>
                  {unscheduledDays.length === preview?.days.length
                    ? "Deze dag heeft geen roosteruren, er wordt 0 uur afgeschreven. Kies een andere datum of gebruik Handmatig om de uren toch vast te leggen."
                    : "Deze aanvraag komt uit op 0 uur. Controleer de periode en het aantal uren."}
                </span>
              </div>
            )}
            {!hasNoRequestedHours && bookedWithoutSchedule && (
              <div className="notice notice-info compact">
                <Clock3 size={17} />
                <span>
                  {unscheduledDays.map((day) => formatDate(day.date)).join(" · ")} heeft
                  geen roosteruren. De handmatig opgegeven uren worden toch
                  afgeschreven.
                </span>
              </div>
            )}
            {preview && preview.balance_after_minutes < 0 && !isOvertime && usesBalance && (
              <div
                className={`notice compact ${
                  preview.negative_balance_allowed ? "notice-info" : "notice-warning"
                }`}
              >
                <AlertTriangle size={17} />
                <span>
                  {preview.negative_balance_allowed
                    ? `Na goedkeuring wordt het saldo ${formatMinutes(
                        preview.balance_after_minutes,
                      )}. Negatief saldo is voor dit type toegestaan.`
                    : "Er is onvoldoende saldo om deze aanvraag nu goed te keuren."}
                </span>
              </div>
            )}
            {preview?.days.some((day) => day.public_holiday) && (
              <div className="notice notice-info compact">
                <Clock3 size={17} />
                <span>
                  {preview.days
                    .filter((day) => day.public_holiday)
                    .map(
                      (day) =>
                        `${formatDate(day.date)}: ${day.public_holiday}${
                          day.public_holiday_deducted
                            ? " (wordt afgetrokken)"
                            : " (0 uur)"
                        }`,
                    )
                    .join(" · ")}
                </span>
              </div>
            )}
            {preview?.school_holidays.length ? (
              <div className="school-holiday-notice">
                <Info size={18} />
                <p>
                  Deze periode overlapt met {preview.school_holidays.join(", ")}
                  {preview.school_regions
                    ? preview.school_regions.length === 1
                      ? `, regio ${regionLabel(preview.school_regions[0])}`
                      : ""
                    : `, regio ${regionLabel(preview.school_region)}`}
                  .
                </p>
                <strong>Schoolvakanties hebben geen invloed op je verlofsaldo.</strong>
              </div>
            ) : null}
            <button
              className="button primary full-width"
              type="submit"
              disabled={submitting || !preview || hasNoRequestedHours}
            >
              <Check size={18} />{" "}
              {submitting
                ? "Opslaan..."
                : isEditing
                ? "Wijziging opslaan"
                : isOvertime
                ? organization
                  ? "Overuren indienen"
                  : "Overuren bijschrijven"
                : organization
                ? "Aanvraag indienen"
                : "Verlof toevoegen"}
            </button>
          </section>
        </aside>
      </form>
    </div>
  );
}

function regionLabel(region?: string) {
  return { north: "Noord", middle: "Midden", south: "Zuid" }[region ?? "middle"];
}
